<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { request } from "@/lib/api";

type Workspace = { id: string; name: string; role: string };

const route = useRoute();
const root = ref<HTMLElement>();
const trigger = ref<HTMLButtonElement>();
const open = ref(false);
const workspaces = ref<Workspace[]>([]);
const pending = ref(true);
const failed = ref(false);
const currentWorkspace = computed(() => {
  const workspaceId = typeof route.params.ws === "string" ? route.params.ws : undefined;
  return workspaces.value.find((workspace) => workspace.id === workspaceId);
});

/* The approved screen puts the signed-in person's role beside their name in the
 * top bar, and this component has already asked /api/workspaces for the list it
 * switches between. Every membership in that answer carries its role, so the
 * shell reads the role from here rather than making the identical request a
 * second time on every workspace route. */
const emit = defineEmits<{ role: [string]; shown: [boolean] }>();
watch(currentWorkspace, (workspace) => emit("role", workspace?.role ?? ""), { immediate: true });
/* Owner 2026-09-24: with only one workspace there is nothing to switch to, so
 * the name is noise ("Default" sat beside the brand). It comes back when a
 * second workspace exists. The shell hides its chevron with it. */
const onlyOne = computed(() => !failed.value && currentWorkspace.value !== undefined && workspaces.value.length < 2);
watch([pending, onlyOne], ([loading, single]) => emit("shown", !loading && !single), { immediate: true });

function closeMenu(): void {
  open.value = false;
  removeDocumentListeners();
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  /* Escape means the nearest open surface, and nothing further out. This
   * listener is on document, so it runs before ReviewView's window listener on
   * the bubble path, and stopping propagation here is what keeps one Escape
   * from both closing this menu and switching the whole view. It stops the
   * outer listeners, not the sibling ones: two menus open at once both still
   * close, because stopPropagation does not touch other listeners on document
   * itself. */
  event.stopPropagation();
  closeMenu();
  trigger.value?.focus();
}

function onDocumentPointerdown(event: PointerEvent): void {
  if (event.target instanceof Node && root.value?.contains(event.target)) return;
  closeMenu();
}

function addDocumentListeners(): void {
  document.addEventListener("keydown", onDocumentKeydown);
  document.addEventListener("pointerdown", onDocumentPointerdown);
}

function removeDocumentListeners(): void {
  document.removeEventListener("keydown", onDocumentKeydown);
  document.removeEventListener("pointerdown", onDocumentPointerdown);
}

function toggleMenu(): void {
  if (open.value) {
    closeMenu();
  } else {
    open.value = true;
    addDocumentListeners();
  }
}

watch(() => route.fullPath, closeMenu);
onUnmounted(removeDocumentListeners);

onMounted(async () => {
  try {
    workspaces.value = (await request<{ workspaces: Workspace[] }>("/api/workspaces")).workspaces;
  } catch {
    workspaces.value = [];
    failed.value = true;
  } finally {
    pending.value = false;
  }
});
</script>

<template>
  <template v-if="pending"></template>
  <span v-else-if="failed" class="workspace-switcher__failure" role="alert">Workspaces could not be loaded.</span>
  <span v-else-if="!currentWorkspace" role="status">This workspace could not be found.</span>
  <template v-else-if="onlyOne"></template>
  <div v-else ref="root" class="workspace-switcher">
    <button
      ref="trigger"
      class="workspace-switcher__trigger hit"
      type="button"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-label="`Switch workspace: ${currentWorkspace.name}`"
      @click="toggleMenu"
    >
      <!-- The name is wrapped because the TRUNCATION has to live on an inner
           element. `overflow: hidden` is what makes the ellipsis work, and it
           also clips the button's own `::after`, which is how `.hit` grows a
           short control's hit box, so with both on the button the overlay was
           clipped to the 22px it was meant to grow past and the class did
           nothing. Measured: still 22px tall with `hit` applied. -->
      <span class="workspace-switcher__name">{{ currentWorkspace.name }}</span>
    </button>
    <div v-if="open" class="workspace-switcher__menu" role="menu">
      <RouterLink
        v-for="workspace in workspaces"
        :key="workspace.id"
        class="workspace-switcher__entry"
        role="menuitem"
        :to="{ name: 'workspace', params: { ws: workspace.id } }"
        :aria-current="workspace.id === currentWorkspace.id ? 'page' : undefined"
      >
        {{ workspace.name }}
      </RouterLink>
    </div>
  </div>
</template>

<style scoped>
.workspace-switcher { position: relative; min-width: 0; }
.workspace-switcher__trigger { font-family: var(--font-head); font-size: 16px; font-weight: 600; letter-spacing: -0.01em; color: var(--ink); }
.workspace-switcher__failure { color: var(--error); }
.workspace-switcher__trigger { display: block; max-width: min(45vw, 20rem); border: 0; background: transparent; padding: 0; cursor: pointer; }
/* The truncation, moved off the button 2026-09-17: see the note in the template.
 * The width it truncates against is still the button's `max-width`, because a
 * block child of a fit-content button is as wide as the button and no wider. */
.workspace-switcher__name { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.workspace-switcher__menu { position: absolute; z-index: var(--z-raised); top: 100%; left: 0; display: grid; width: min(max-content, calc(100vw - 40px)); max-width: calc(100vw - 40px); overflow-x: auto; margin-top: 6px; padding: 6px; border: 1px solid var(--line); border-radius: var(--radius-control); background: var(--card); box-shadow: var(--shadow-pop); }
.workspace-switcher__entry { max-width: 100%; overflow: hidden; padding: 6px 8px; border-radius: var(--radius-chip); color: var(--ink); text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
.workspace-switcher__entry[aria-current="page"] { background: var(--primary-canvas); }
</style>
